import { generatePrivateKey, privateKeyToAccount, type PrivateKeyAccount } from 'viem/accounts';
import type { Address, Hex, WalletClient } from 'viem';

import { sessionKeyManagerAbi } from '../../../offchain/src/abi.ts';
import { config, deployment } from '../config.ts';
import { walletReader } from './tx.ts';
import { deadlineIn, newNonce, run } from './intents.ts';
import { relayer } from './relayer.ts';

// A session key is a throwaway key kept in this browser. It can open, close, withdraw to the user's own
// HyperCore account and stake for the user, never send funds elsewhere: the contracts pin every
// destination to the user. It expires onchain and can be revoked from the wallet at any time.

export type Session = { account: PrivateKeyAccount; expiry: number };

type Stored = { key: Hex; expiry: number };

const storageKey = (user: Address) => `papurr:session:${config.chainId}:${deployment.sessionKeyManager}:${user}`;

function read(user: Address): Stored | undefined {
  try {
    const raw = localStorage.getItem(storageKey(user));
    return raw ? (JSON.parse(raw) as Stored) : undefined;
  } catch {
    return undefined;
  }
}

function write(user: Address, s: Stored | undefined): void {
  try {
    if (s) localStorage.setItem(storageKey(user), JSON.stringify(s));
    else localStorage.removeItem(storageKey(user));
  } catch {
    // private mode: the session only lives in memory, the user re-enables it next visit
  }
}

/** Stored session, if its key is still registered onchain and not about to expire. */
export async function loadSession(user: Address): Promise<Session | undefined> {
  const s = read(user);
  if (!s) return undefined;
  const account = privateKeyToAccount(s.key);
  const expiry = await relayer.sessionKeyExpiry(user, account.address);
  if (expiry <= Date.now() / 1000 + 60) {
    write(user, undefined);
    return undefined;
  }
  return { account, expiry };
}

/** Creates a key and registers it through the relayer: one wallet signature, no gas. */
export async function enableSession(user: Address, wallet: WalletClient): Promise<Session> {
  const maxDuration = await relayer.maxSessionDuration();
  const duration = Math.min(config.sessionDays * 86_400, maxDuration);
  const expiry = Math.floor(Date.now() / 1000) + duration;
  const key = generatePrivateKey();
  const account = privateKeyToAccount(key);
  await run(
    {
      action: 'registerSessionKey',
      request: { user, key: account.address, expiry: BigInt(expiry), nonce: newNonce(), deadline: deadlineIn(120) },
    },
    { kind: 'wallet', client: wallet },
  );
  write(user, { key, expiry });
  return { account, expiry };
}

/** Revokes onchain (a wallet transaction) and forgets the key. */
export async function revokeSession(user: Address, wallet: WalletClient, s: Session): Promise<void> {
  const hash = await wallet.writeContract({
    account: wallet.account!,
    chain: config.chain,
    address: deployment.sessionKeyManager,
    abi: sessionKeyManagerAbi,
    functionName: 'revokeSessionKey',
    args: [s.account.address],
  });
  await walletReader(wallet).waitForTransactionReceipt({ hash });
  write(user, undefined);
}
